import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Save, X, Plus, Trash2, Box } from "lucide-react";
import { KV } from "./atoms";
import CollapsibleCard from "./CollapsibleCard";

/**
 * EditableBRepCard — viewer-tab card for B-Rep features extracted by OCP.
 * In view mode it displays bbox/volume/topology + a hover-syncable hole list.
 * In edit mode each hole becomes an inline input row and the user can add /
 * remove holes manually. Saving emits the diff via ``onSave`` so the parent
 * persists it on the quote.
 */
export default function EditableBRepCard({ features, onSave, onHoverHole, hovered }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const initialHoles = features?.candidate_holes || [];
  const [draftHoles, setDraftHoles] = useState(initialHoles);
  const [saving, setSaving] = useState(false);

  if (!features?.ok) return null;
  const partBBox = features?.bbox_mm || null;

  const startEdit = () => {
    setDraftHoles(JSON.parse(JSON.stringify(initialHoles)));
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraftHoles(initialHoles);
  };
  const updateHole = (i, key, value) => {
    setDraftHoles((curr) => curr.map((h, idx) => idx === i ? { ...h, [key]: value } : h));
  };
  const deleteHole = (i) => {
    setDraftHoles((curr) => curr.filter((_, idx) => idx !== i));
  };
  const addHole = () => {
    setDraftHoles((curr) => [...curr, {
      diameter_mm: 5, depth_mm: 10,
      axis_direction: [0, 0, 1],
      // No center_xyz on user-added holes → 3D viewer falls back to legend mode.
    }]);
  };
  const submit = async () => {
    setSaving(true);
    const clean = draftHoles
      .map((h) => ({
        ...h,
        diameter_mm: parseFloat(h.diameter_mm) || 0,
        depth_mm: parseFloat(h.depth_mm) || 0,
      }))
      .filter((h) => h.diameter_mm > 0);
    try {
      await onSave({ candidate_holes_override: clean });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const holesShown = editing ? draftHoles : initialHoles;

  const headerActions = !editing ? (
    <button
      type="button"
      onClick={startEdit}
      className="p-1 rounded text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
      title={t("viewerTab.editHoles")}
      data-testid="brep-edit-btn"
    >
      <Pencil size={12}/>
    </button>
  ) : (
    <div className="flex items-center gap-1">
      <button type="button" onClick={cancel} className="p-1 rounded text-slate-400 hover:text-rose-300" title={t("common.cancel")} data-testid="brep-cancel-btn">
        <X size={12}/>
      </button>
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
        title={t("common.save")}
        data-testid="brep-save-btn"
      >
        <Save size={12}/>
      </button>
    </div>
  );

  return (
    <CollapsibleCard
      testid="brep-features-card"
      className="border-cyan-500/30"
      titleClassName="text-cyan-300"
      storageKey="brep"
      icon={<Box size={14}/>}
      title="B-Rep (OpenCascade)"
      actions={headerActions}
    >
      <div className="space-y-2 text-sm">
        {partBBox && (
          <KV k="Bounding box reale" v={`${partBBox.length} × ${partBBox.width} × ${partBBox.height} mm`}/>
        )}
        {features.volume_cm3 != null && <KV k="Volume reale" v={`${features.volume_cm3} cm³`}/>}
        {features.surface_area_mm2 != null && <KV k="Superficie" v={`${features.surface_area_mm2} mm²`}/>}
        {features.topology && (
          <KV k="Topologia" v={`${features.topology.solids}S · ${features.topology.faces}F · ${features.topology.edges}E`}/>
        )}
        {features.geometry_classification_heuristic && (
          <KV k="Classe B-Rep" v={features.geometry_classification_heuristic}/>
        )}

        {holesShown.length > 0 && (
          <div className="pt-2 border-t border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tech text-[10px]">{t("brepCard.holesDetected", { count: holesShown.length })}</div>
              {editing && (
                <button
                  type="button"
                  onClick={addHole}
                  className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="brep-add-hole-btn"
                >
                  <Plus size={10}/> Aggiungi foro
                </button>
              )}
            </div>
            <ul className="space-y-1 text-[12px] font-mono-data text-cyan-200/90">
              {holesShown.map((h, i) => editing ? (
                <li key={`hole-edit-${i}`} className="flex items-center gap-2 px-2 py-1 rounded bg-slate-800/40">
                  <span className="text-slate-500 w-6">#{i + 1}</span>
                  <span className="text-slate-400 text-[10px]">Ø</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={h.diameter_mm}
                    onChange={(e) => updateHole(i, "diameter_mm", e.target.value)}
                    className="w-16 px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`brep-hole-d-${i}`}
                  />
                  <span className="text-slate-500 text-[10px]">×</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={h.depth_mm}
                    onChange={(e) => updateHole(i, "depth_mm", e.target.value)}
                    className="w-16 px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`brep-hole-l-${i}`}
                  />
                  <span className="text-slate-500 text-[10px]">mm</span>
                  <button
                    type="button"
                    onClick={() => deleteHole(i)}
                    className="ml-auto p-0.5 text-rose-400 hover:text-rose-300"
                    title="Elimina"
                    data-testid={`brep-hole-del-${i}`}
                  >
                    <Trash2 size={11}/>
                  </button>
                </li>
              ) : (
                <li
                  key={`hole-${i}`}
                  onMouseEnter={() => onHoverHole?.(`hole-${i}`)}
                  onMouseLeave={() => onHoverHole?.(null)}
                  className={`flex items-center justify-between px-2 py-1 rounded transition-colors cursor-pointer ${
                    hovered === `hole-${i}` ? "bg-cyan-500/15" : "hover:bg-slate-800/50"
                  }`}
                  data-testid={`brep-hole-row-${i}`}
                >
                  <span>#{i + 1}</span>
                  <span>Ø {h.diameter_mm} × {h.depth_mm} mm</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}
